/**
 * Cat & Mouse — what goes on the board (specs/cat-mouse-mode.md §4, §7, §8.3).
 *
 * `board.ts` decides the shape, this file decides the contents, and `CatScene` only draws what the
 * two return. Phaser-free, like `pairs/deal.ts` and `labyrinth/deal.ts`.
 */
import { CAT } from '@/game/config';
import type { Pack } from '@/data/packs/types';
import { cellKey, routeTo, reachable, type Cell, type Rng } from './board';
import { formsOf, matches, type LetterClass } from './membership';
import { pickDoorWord, type DoorWord } from './word';

export interface CatDoor {
  /** Row of the last column the door is attached to. */
  row: number;
  /**
   * What the door wears — every active class's letterforms, or '' on a word board, whose door wears
   * a picture instead.
   *
   * **There is exactly one door.** A two-class board briefly had one door per class, to put the
   * grouping in space; it read as two exits and was one more thing to decide at the moment the child
   * had just succeeded. One exit, wearing everything the board wants (§4.4).
   */
  label: string;
}

export interface CatBoard {
  /** 1-based, 1…CAT.boards. */
  board: number;
  cols: number;
  rows: number;
  entry: Cell;
  /**
   * `grid[row][col]` — one grapheme cluster per cell, or `''` for **empty floor**.
   *
   * Floor is walked for free and is most of the board. Without it the cat cannot cross: every cell
   * that is not the target it wants costs a fish, so a three-fish cat on a 5x4 grid never reaches
   * anything. See CAT.decoys.
   */
  grid: string[][];
  doors: CatDoor[];
  /** Cell key → the class id that cell satisfies. Every other cell is a decoy. */
  targets: Map<string, string>;
  /** The active classes, in door order. Empty on the word board. */
  classes: LetterClass[];
  /**
   * Classes whose glyphs are on this board as DECOYS.
   *
   * They count as *met* (§8.2) even though they are not drilled: the child has looked straight at
   * them while deciding not to step on them, and "met" is the picture doors' alphabet (§7.2). Without
   * this the alphabet grew only by the one or two letters each board drills, and the picture door —
   * the whole ending of the mode — stayed undealable for four or five runs, each of which costs a
   * treat.
   */
  decoyClassIds: string[];
  /**
   * Word board only: the cell keys in the order they must be stepped on. A target tapped out of
   * turn is wrong, which is what makes the last board a *word* rather than a set.
   *
   * It is the *letters* that are ordered, not these cells: two cells carrying the same glyph are
   * interchangeable, because they are the same letter (`isNextTarget`, §7.1).
   */
  order: string[] | null;
  /** Word board only: the word, its picture id, and whether its steps compose (§7.3). */
  word: DoorWord | null;
}

export interface DealInput {
  /** 1-based board number within the run. */
  board: number;
  /** Every letter of the language, in `rank` order. */
  classes: LetterClass[];
  /** The gated window of unknown classes this run drills (§8.2). */
  activeIds: string[];
  /** Classes the child has already cleared — the review tier of the decoy pool (§8.3). */
  knownIds: Set<string>;
  /**
   * Every class the child has EVER met — the picture doors' alphabet (§7.2). Wider than
   * `knownIds ∪ activeIds`, which is what it falls back to: a letter met in an earlier run and not
   * yet mastered still belongs in the word the run ends on.
   */
  metIds?: Set<string>;
  lang: string;
  /** The picture pool, for the word boards. Null is handled: the board falls back (§7.2). */
  picturePool: Pack | null;
  /** Word ids this run has already spelled — no two picture doors may wear the same word. */
  usedWordIds?: Set<string>;
  rng: Rng;
}

const shuffled = <T>(items: T[], rng: Rng): T[] => {
  const out = items.slice();
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
};

const rung = (board: number, len: number): number =>
  Math.min(Math.max(1, Math.floor(board)), len) - 1;

/**
 * Deal one board.
 *
 * Never returns null: a picture door falls back to an ordinary board when no word qualifies (§7.2),
 * and a language with too few classes to fill a two-door board falls back to one door. A treat that
 * was spent must always produce a run.
 */
export function dealBoard(input: DealInput): CatBoard {
  const { board, classes, activeIds, knownIds, lang, picturePool, rng } = input;
  const i = rung(board, CAT.grids.length);
  const [cols, rows] = CAT.grids[i];
  const entry: Cell = [0, Math.floor(rng() * rows)];

  const wantClasses = CAT.classes[i];
  const byId = new Map(classes.map((c) => [c.id, c]));

  // ── the word board ─────────────────────────────────────────────────────────
  if (wantClasses === 0) {
    const metIds = input.metIds ?? new Set<string>([...knownIds, ...activeIds]);
    const word = pickDoorWord(picturePool, classes, metIds, lang, rng, input.usedWordIds ?? new Set());
    if (word) return dealWordBoard({ board, cols, rows, entry, word, classes, metIds, lang, rng });
    // Nothing qualifies yet — a first-ever run, where two letters are known. Deal an ordinary
    // board instead and let the picture door wait for the next run. This branch is the one
    // `word.test.ts` is pointed at.
  }

  // ── an ordinary board ──────────────────────────────────────────────────────
  const active = activeIds.map((id) => byId.get(id)).filter((c): c is LetterClass => !!c);
  const nClasses = Math.max(1, Math.min(wantClasses || 1, active.length));
  // Pair classes that are confusable with each other where the language offers it (§4.4): the
  // decoys then come for free and the pairing is the discrimination the child is failing at. The
  // rival may be a letter already KNOWN — see pickClasses for why it has to be allowed to be.
  const known = classes.filter((c) => knownIds.has(c.id));
  const chosen = pickClasses(active, [...active, ...known], nClasses, rng);

  const nTargets = Math.max(nClasses, CAT.targets[i] || 2);
  const targetGlyphs: { glyph: string; classId: string }[] = [];
  for (let t = 0; t < nTargets; t++) {
    const cls = chosen[t % chosen.length];
    const forms = shuffled(formsOf(cls, lang), rng);
    // Prefer a form this class has not put on the board yet — the point of the mode is that two
    // DIFFERENT drawings are one letter, so a board of three identical `K`s teaches nothing.
    const used = new Set(targetGlyphs.filter((g) => g.classId === cls.id).map((g) => g.glyph));
    targetGlyphs.push({ glyph: forms.find((f) => !used.has(f)) ?? forms[0], classId: cls.id });
  }

  const decoyPool = buildDecoyPool(board, chosen, classes, byId, knownIds, lang);
  const doors = doorsFor(chosen, rows, rng);
  const placed = place(targetGlyphs.length, cols, rows, entry, doors, CAT.decoys[i], null, rng);

  const grid = emptyGrid(cols, rows);
  const targets = new Map<string, string>();
  targetGlyphs.forEach((t, n) => {
    const cell = placed.targets[n];
    grid[cell[1]][cell[0]] = t.glyph;
    targets.set(cellKey(cell), t.classId);
  });
  const decoyClassIds = new Set<string>();
  for (const cell of placed.decoys) {
    const glyph = pickDecoy(decoyPool, chosen, rng);
    grid[cell[1]][cell[0]] = glyph;
    const owner = classes.find((c) => matches(glyph, c));
    if (owner) decoyClassIds.add(owner.id);
  }

  return {
    board, cols, rows, entry, grid, doors, targets, classes: chosen,
    decoyClassIds: [...decoyClassIds], order: null, word: null,
  };
}

/**
 * Where the letters go, and — just as importantly — where they do NOT.
 *
 * The promise being kept (§5.4) is that a child who reads carefully can finish **any** board without
 * losing a fish: there must always be a walk to every target, and then to a door, that never steps
 * on a letter it was not sent for.
 *
 * The first implementation kept that promise by *reserving* the shortest path to every goal and
 * refusing to put a decoy on any of it. It worked and it was useless: on a 4x4 board with two doors
 * and four targets the reserved paths covered the whole grid, so the board was dealt with **no
 * decoys at all** — every letter on it was one to collect, and there was nothing to read past.
 *
 * So decoys are placed one at a time and each one is *tested*: lay it down, re-walk the whole board
 * avoiding it, and keep it only if a clean route still exists. Slower on paper — a handful of BFS
 * runs over twenty cells — and it fills the board instead of emptying it.
 */
function place(
  count: number, cols: number, rows: number, entry: Cell, doors: CatDoor[],
  decoyCount: number, steps: string[] | null, rng: Rng,
): { targets: Cell[]; decoys: Cell[] } {
  const free = allCellsExcept(cols, rows, entry);
  const doorCells = doors.map((d) => [cols - 1, d.row] as Cell);

  for (let attempt = 0; attempt < 24; attempt++) {
    const shuffledCells = shuffled(free, rng);
    const targets = shuffledCells.slice(0, count);
    if (!cleanRouteExists(entry, targets, doorCells, new Set(), cols, rows, steps)) continue;

    const taken = new Set(targets.map(cellKey));
    const decoys: Cell[] = [];
    const placed = new Set<string>();
    for (const cell of shuffledCells) {
      if (decoys.length >= decoyCount) break;
      const key = cellKey(cell);
      if (taken.has(key)) continue;
      placed.add(key);
      if (cleanRouteExists(entry, targets, doorCells, placed, cols, rows, steps)) decoys.push(cell);
      else placed.delete(key);
    }
    return { targets, decoys };
  }

  // Every placement walled something off (a tiny grid with many targets). Deal it with NO decoys
  // rather than an unfinishable board — an easy board beats an impossible one.
  return { targets: shuffled(free, rng).slice(0, count), decoys: [] };
}

/**
 * **The way to a target may never be blocked.** This is the function that promises it.
 *
 * The check has to be *order-independent* on an ordinary board, and it used to not be: it walked
 * the targets in the order they were dealt, so the board was only proved finishable for that one
 * order, and a child collecting them in any other order could strand themselves behind a decoy.
 *
 * So the two board kinds get the two different questions they actually ask:
 *
 *  - **An ordinary board** (any order): every target must lie in the region reachable from the cat
 *    **without ever stepping on a decoy**. Targets themselves are passable here, because stepping on
 *    one collects it rather than costing a fish — so one region, computed once, answers it for every
 *    order the child could possibly choose.
 *  - **A word board** (`steps` given): a target tapped out of turn *does* cost a fish, so the walk
 *    is checked step by step in the word's own order, with the letters not yet wanted treated as
 *    obstacles alongside the decoys — and once for **every order the word allows**, which is more
 *    than one as soon as a letter repeats (see `stepOrders`).
 *
 * The walk to the door is not checked against decoys at all: by then it cannot be blocked, because
 * collecting the last letter puts the rest to sleep and opens the way out (`readyForDoor`, §5.3).
 */
export function cleanRouteExists(
  entry: Cell, targets: Cell[], doorCells: Cell[], decoys: Set<string>,
  cols: number, rows: number, steps: string[] | null = null,
): boolean {
  if (!steps) {
    const region = reachable(entry, cols, rows, decoys);
    if (!targets.every((t) => region.has(cellKey(t)))) return false;
    return doorCells.some((door) => routeTo(door, entry, cols, rows) !== null);
  }
  return stepOrders(targets, steps)
    .every((order) => orderedRouteExists(entry, order, doorCells, decoys, cols, rows));
}

/** One fixed collection order: reach each target in turn without stepping on anything else. */
function orderedRouteExists(
  entry: Cell, targets: Cell[], doorCells: Cell[], decoys: Set<string>,
  cols: number, rows: number,
): boolean {
  let at = entry;
  const emptied = new Set<string>();
  for (const target of targets) {
    const blocked = new Set(decoys);
    for (const other of targets) {
      const key = cellKey(other);
      if (!emptied.has(key)) blocked.add(key);
    }
    if (!routeTo(target, at, cols, rows, blocked)) return false;
    emptied.add(cellKey(target));
    at = target;
  }
  return doorCells.some((door) => routeTo(door, at, cols, rows) !== null);
}

/**
 * Every order a child may collect this word in — the dealt one, plus the swaps between cells
 * carrying the **same letter**.
 *
 * `Boot` puts two `o`s on the board and either may be taken first (§7.1), so proving the dealt
 * order reachable proves nothing about the order the child actually walks: the second `o` may sit
 * behind a decoy that the first one's route never had to pass. One `Boot` is two orders, `Hallo`
 * two, and a word without a repeated letter is the single order it was dealt in.
 *
 * `targets` is parallel to `steps`: `targets[n]` is the cell carrying `steps[n]`.
 */
export function stepOrders(targets: Cell[], steps: string[]): Cell[][] {
  const groups = new Map<string, number[]>();
  steps.forEach((g, i) => groups.set(g, [...(groups.get(g) ?? []), i]));

  let orders: Cell[][] = [targets.slice()];
  for (const slots of groups.values()) {
    if (slots.length < 2) continue;
    // Beyond the cap, check the dealt order alone rather than enumerate. No word in any pack comes
    // close — five cells is the whole train (§7.1) — and `deal.test.ts` holds that line.
    if (orders.length * factorial(slots.length) > STEP_ORDER_CAP) continue;
    orders = orders.flatMap((order) => permutations(slots).map((perm) => {
      const out = order.slice();
      slots.forEach((slot, k) => { out[slot] = order[perm[k]]; });
      return out;
    }));
  }
  return orders;
}

/** Enough for a five-cell word whose every letter is the same one (5! = 120). */
export const STEP_ORDER_CAP = 120;

function factorial(n: number): number {
  let out = 1;
  for (let i = 2; i <= n; i++) out *= i;
  return out;
}

function permutations<T>(xs: T[]): T[][] {
  if (xs.length <= 1) return [xs.slice()];
  return xs.flatMap((x, i) =>
    permutations([...xs.slice(0, i), ...xs.slice(i + 1)]).map((rest) => [x, ...rest]));
}

/** The word board: the picture on the door, its letters in order on the grid. */
function dealWordBoard(a: {
  board: number; cols: number; rows: number; entry: Cell;
  word: DoorWord; classes: LetterClass[]; metIds: Set<string>; lang: string; rng: Rng;
}): CatBoard {
  const { board, cols, rows, entry, word, classes, metIds, lang, rng } = a;
  const doors: CatDoor[] = [{ row: Math.floor(rng() * rows), label: '' }];
  const placed = place(
    word.steps.length, cols, rows, entry, doors, CAT.decoys[CAT.decoys.length - 1], word.steps, rng,
  );
  const grid = emptyGrid(cols, rows);
  const targets = new Map<string, string>();
  const order: string[] = [];

  word.steps.forEach((step, n) => {
    const cell = placed.targets[n];
    grid[cell[1]][cell[0]] = step;
    const key = cellKey(cell);
    targets.set(key, `step-${n}`);
    order.push(key);
  });

  // Decoys are met letters that are NOT in the word — a wrong step here is picking a letter the
  // word does not want. (Picking a right one too early is also wrong, and costs the same fish.)
  const taken = new Set(word.steps);
  const met = classes.filter((c) => metIds.has(c.id));
  const pool = met.flatMap((c) => formsOf(c, lang)).filter((g) => !taken.has(g));
  const decoyClassIds = new Set<string>();
  if (pool.length > 0) {
    for (const cell of placed.decoys) {
      const glyph = pool[Math.floor(rng() * pool.length)];
      grid[cell[1]][cell[0]] = glyph;
      const owner = classes.find((c) => matches(glyph, c));
      if (owner) decoyClassIds.add(owner.id);
    }
  }

  return {
    board, cols, rows, entry, grid, doors, targets, classes: [],
    decoyClassIds: [...decoyClassIds], order, word,
  };
}

/**
 * Choose the board's classes, preferring a pair that are each other's look-alikes.
 *
 * `b`/`d`, `ㅏ`/`ㅓ`, `シ`/`ツ` — pairing the two letters a child actually confuses is what makes a
 * two-door board a discrimination exercise rather than two easy boards side by side.
 *
 * **The rival is looked for OUTSIDE the active window as well, among letters already known.** That
 * is not an optimisation, it is the resolution of a conflict between two rules this mode holds at
 * once: teaching order deliberately separates every confusable pair by at least three ranks (§8.2.1
 * — `b` and `d` are never met in the same week), so a window of four consecutive ranks can never
 * contain a rival pair, and a strictly-in-window pairing produced a look-alike board **0 times in
 * 40** when it was first measured. Pairing the new letter against an *old* rival is also the better
 * exercise: the contrast a child needs is with the letter they already think they know.
 */
export function pickClasses(active: LetterClass[], pool: LetterClass[], n: number, rng: Rng): LetterClass[] {
  const order = shuffled(active, rng);
  const first = order[0];
  if (n <= 1) return [first];
  const rivals = shuffled(pool, rng)
    .filter((c) => c.id !== first.id && (first.prefer.includes(c.id) || c.prefer.includes(first.id)));
  const rival = rivals[0];
  const rest = order.filter((c) => c.id !== first.id && c.id !== rival?.id);
  return [first, ...(rival ? [rival] : []), ...rest].slice(0, n);
}

/**
 * The decoy pool, in the priority order of §8.3: the targets' look-alikes, then classes the child
 * already knows (where the review happens), then anything else. From `CAT.lookalikeOnlyFrom` the
 * third tier is dropped and the board stops being "find the K".
 *
 * A glyph that would satisfy an active class is never a decoy — that is the one hard rule.
 */
export function buildDecoyPool(
  board: number,
  chosen: LetterClass[],
  classes: LetterClass[],
  byId: Map<string, LetterClass>,
  knownIds: Set<string>,
  lang: string,
): string[] {
  const chosenIds = new Set(chosen.map((c) => c.id));
  const preferred = new Set(chosen.flatMap((c) => c.prefer));
  const tier = (c: LetterClass): number => {
    if (preferred.has(c.id)) return 0;
    if (knownIds.has(c.id)) return 1;
    return 2;
  };
  const maxTier = board >= CAT.lookalikeOnlyFrom ? 1 : 2;

  let pool = classes
    .filter((c) => !chosenIds.has(c.id) && tier(c) <= maxTier)
    .flatMap((c) => formsOf(c, lang));
  // Every language must be able to fill a grid, so a narrow look-alike tier widens rather than
  // dealing an empty cell.
  if (pool.length < 4) {
    pool = classes.filter((c) => !chosenIds.has(c.id)).flatMap((c) => formsOf(c, lang));
  }
  void byId;
  return [...new Set(pool)].filter((g) => !chosen.some((c) => matches(g, c)));
}

function pickDecoy(pool: string[], chosen: LetterClass[], rng: Rng): string {
  if (pool.length === 0) return '·';
  for (let tries = 0; tries < 8; tries++) {
    const g = pool[Math.floor(rng() * pool.length)];
    if (!chosen.some((c) => matches(g, c))) return g;
  }
  return pool[0];
}

function doorsFor(chosen: LetterClass[], rows: number, rng: Rng): CatDoor[] {
  // One door, wearing every letter the board wants. Two classes are two pairs on one sign rather
  // than two exits — see CatDoor.label.
  return [{ row: Math.floor(rng() * rows), label: chosen.map((c) => c.label).join(' · ') }];
}

function emptyGrid(cols: number, rows: number): string[][] {
  return Array.from({ length: rows }, () => Array.from({ length: cols }, () => ''));
}

function allCellsExcept(cols: number, rows: number, skip: Cell): Cell[] {
  const out: Cell[] = [];
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      if (c === skip[0] && r === skip[1]) continue;
      out.push([c, r]);
    }
  }
  return out;
}
